// @see https://cloudinary.com/documentation/resizing_and_cropping#control_gravity
import type { Responsive } from '#types/common'
import type { BackgroundAuto, ImageTransformationsAlignment } from '#types/imageTransformations'
import ds from '#root/design-system'

/**
 * Parameters for Cloudinary image transformations. Controls alignment, cropping,
 * format, dimensions, quality, and sharpening applied to a single image URL.
 */
export interface ImageTransformations {
  /** The (compass) direction to align the image to. */
  align?: ImageTransformationsAlignment
  alignFill?: ImageTransformationsAlignment
  /**
   * Target aspect ratio (`width / height`), e.g. `0.78`. Cloudinary derives the dimension that is
   * not given, so setting this drops `width` and keeps `height` - pass both and let it choose.
   * It also suppresses the region's `defaultTransformation`, whose own crop and ratio would
   * otherwise collide with this one inside the same token component.
   */
  aspectRatio?: number | string
  /** Background to fill with when padding to an aspect ratio, e.g. `'gen_fill'` for generative fill. */
  background?: 'gen_fill'
  /** A background property used in tandem with align (c_pad) if the image is a different aspect ratio than presets  */
  backgroundAuto?: BackgroundAuto
  /** Raw Cloudinary transformation string from the API (e.g. "c_fill,w_500,f_auto"). Inserted before config tokens so config overrides API. */
  assetTransformation?: string
  /** The cropping method to use. */
  crop?: 'fill' | 'scale'
  /** Whether or not to include the default 2.0 dpr  */
  excludeDPR?: boolean
  /** The format of the image. */
  format?: 'auto' | 'auto:image' | 'png'
  /** The height of the image, in pixels. */
  height?: number
  quality?: number | 'auto'
  /** Sets both the width and height at the same time. */
  size?: number
  /**
   * Applying sharpness and dpr to fix blurry images in TNF Nora PLP and PDP images
   * reference: https://digital.vfc.com/jira/browse/GLOBAL15-103845
   */
  sharpen?: number
  /** The width of the image, in pixels. */
  width?: number
  unSharpMask?: number
}

/**
 * Variant of `ImageTransformations` where width, height, and size accept
 * breakpoint-keyed responsive values instead of a single number.
 */
export interface ResponsiveImageTransformations extends Omit<ImageTransformations, 'aspectRatio' | 'height' | 'width' | 'size'> {
  /** The box a card paints into can be a different shape per breakpoint, so the ratio is too. */
  aspectRatio?: Responsive<number>
  height?: Responsive<number>
  size?: Responsive<number>
  width?: Responsive<number>
}

/**
 * Cloudinary transformation tokens prepended to every URL produced by `getImageTransformations`.
 * Mutate this array at app bootstrap to apply project-wide defaults (e.g. forced quality presets).
 * @category Utils
 */
export const defaultTransformationTokens: string[] = []

const transformationTokenMap: Record<keyof Omit<ImageTransformations, 'excludeDPR' | 'assetTransformation'>, string> = {
  // Leads the component so the ratio is read before the dimension it derives the other side from.
  aspectRatio: 'ar_',
  align: 'c_pad,g_',
  alignFill: 'c_fill,g_',
  background: 'b_',
  backgroundAuto: 'b_auto:',
  crop: 'c_',
  format: 'f_',
  height: 'h_',
  quality: 'q_',
  sharpen: 'e_sharpen:',
  size: 'w_',
  unSharpMask: 'e_unsharp_mask:',
  width: 'w_',
}

const DEFAULT_NAMED_TRANSFORMATION = 't_img'

/**
 * A helper function for extracting the transformation values from the consumer-provided options.
 * @param t The transformations to apply to the image.
 * @returns {string[]} An array of transformation values (e.g. ['f_auto', 'c_scale', 'w_650']).
 * @category Utils
 */
const generateTransformationValues = (t: ImageTransformations): string[] => {
  const transformationValues = new Set(defaultTransformationTokens)
  const hasRedundantDimensions = t.size && (t.height || t.width)

  /* If we do not receive an override format, will use auto by default */
  t.format ??= 'auto'
  t.sharpen ??= 60

  // Sharpen and unSharpMask options do not coexist. So, setting the sharpen to 0 when unSharpMask is present
  if (t.unSharpMask) t.sharpen = 0

  if (hasRedundantDimensions) {
    delete t.height
    delete t.width
    log.debug('[@domains/core/utils/getImageTransformations] Both a size and height/width value was set. Defaulting to size.')
  }

  // Cloudinary derives the missing side from the ratio, and pinning both would fight it - so the
  // ratio picks the dimension. Height is kept because a card's height is what its grid row fixes.
  if (t.aspectRatio && t.width && t.height) {
    delete t.width
    log.debug('[@domains/core/utils/getImageTransformations] An aspectRatio was set alongside width and height. Deriving width from the ratio.')
  }

  Object.entries(transformationTokenMap).forEach(([key, token]) => {
    if (t[key] && key !== 'excludeDPR') {
      const value = `${token}${t[key]}`
      transformationValues.add(value)
    }
  })

  return [...transformationValues]
}

/**
 * Applies the Canvas-standard Cloudinary transformation pipeline to an image URL.
 * Prefer this over `addCloudinaryTransformations` for standard image rendering —
 * it applies Canvas defaults and wires into the asset transformation config.
 * @param url URL used to construct new URL object.
 * @param transformations The transformations to apply to the image.
 * @example
 * Images will arrive in the following formats:
 * 'https://assets.timberland.eu/images/t_Thumbnail/v1702342099/TB0A27PQ001-HERO/Brooklyn-Hiker-for-Men-in-Black.png'
 *
 * The default named transformation (e.g. t_Thumbnail) will be replaced with t_img, followed by a set of transformations.
 * For example, if the image above was resized to a width of 650px, the URL would be transformed to:
 * 'https://assets.timberland.eu/images/t_img/f_auto,c_scale,w_650/v1702342099/TB0A27PQ001-HERO/Brooklyn-Hiker-for-Men-in-Black.png'
 * @category Utils
 * @see addCloudinaryTransformations
 * @see https://cloudinary.com/documentation/transformation_reference
 * @returns {string} The transformed image URL.
 */

export const getImageTransformations = (url: string = '', transformations: ImageTransformations = {}): string => {
  if (!url) {
    log.debug('[@domains/core/utils/getImageTransformations] Please provide a url.')
    return ''
  }

  if (url.includes(DEFAULT_NAMED_TRANSFORMATION)) {
    log.debug('[@domains/core/utils/getImageTransformations] No op. The transformations have already been applied.')
    return url
  }
  try {
    const { defaultTransformation = '' } = useAppConfig().assetTransformations ?? {}
    const enableApiAssetTransformations = useFeatureFlags().enableApiAssetTransformations
    const baseUrl = new URL(url)
    // Extract assetTransformation from the URL hash when the backend cannot provide it as a separate
    // field (e.g. '#assetTransformation=c_pad,b_gen_fill;?$PLP-IMAGE$'). Everything from ';' onwards
    // (cache-busting suffixes, etc.) is ignored. The hash is stripped from the output URL.
    const hashAssetTransformation = baseUrl.hash.match(/#assetTransformation=([^;?&]*)/)?.[1]
    baseUrl.hash = ''
    const urlPathValues = baseUrl.pathname.split('/')

    let _: string, ___: string, path: string | undefined, type: string
    let rest: string[] = []

    if (urlPathValues.includes('images', 1) || urlPathValues.includes('videos', 1)) {
      [_, type, ___, ...rest] = urlPathValues
    }
    else if (urlPathValues.includes('upload', 2)) {
      [_, type, path, ...rest] = urlPathValues
    }
    else {
      log.debug('[@domains/core/utils/getImageTransformations] The URL does not match the expected format.')
      return url
    }

    if (type === 'videos') transformations.format ??= 'auto:image'

    // Applying 2x image size to all DPR levels for increased quality of the image
    // reference for details: https://digital.vfc.com/jira/browse/GLOBAL15-91223
    // Adds option to remove this dpr size vie excludeDPR boolean
    // reference for details: https://digital.vfc.com/jira/browse/GLOBAL15-104413

    // Inline assetTransformation (from SFCC field or URL hash) or defaultTransformation (from region
    // config) at the start of the single token component so crop, aspect ratio and dimensions are
    // applied together. assetTransformation takes precedence over defaultTransformation when both are present.
    // An explicit aspectRatio is a per-image decision about the box the image paints into, so it
    // must not land next to a region-wide default carrying its own crop and ratio (thenorthface
    // ships `c_fill,ar_4:5`) - two `c_`/`ar_` tokens in one component would resolve only by
    // Cloudinary's precedence. A per-image assetTransformation still wins, as it does today.
    const fallbackTransformation = transformations.aspectRatio ? '' : defaultTransformation
    const leadingTokens = enableApiAssetTransformations
      ? (transformations.assetTransformation || hashAssetTransformation || fallbackTransformation)?.split(',').filter(Boolean) ?? []
      : []
    baseUrl.pathname = `${type}${path ? `/${path}` : ''}/${DEFAULT_NAMED_TRANSFORMATION}/${[...leadingTokens, ...generateTransformationValues(transformations)].join(',')}${transformations.excludeDPR ? '' : '/dpr_2.0'}/${rest.join('/')}`

    return decodeURIComponent(`${baseUrl}`)
  }
  catch (error: any) {
    log.error(`[@domains/core/utils/getImageTransformations] Unable to transform the provided URL: ${url}.`, error)
    return ''
  }
}

/**
 * Generates a per-breakpoint set of Cloudinary URLs by calling `getImageTransformations` once per
 * breakpoint defined in the design system, picking up the per-breakpoint `width`/`height`/`size`
 * values from the responsive transformation object.
 * @param url - The original Cloudinary image URL.
 * @param t - Responsive transformations. `width`, `height` and `size` accept `Responsive<number>`;
 *            other tokens are applied uniformly to every breakpoint.
 * @returns A `Responsive<string>` map of breakpoint → transformed URL. Breakpoints without a
 *          width/height/size entry are omitted.
 * @category Utils
 */
export const getResponsiveImageTransformations = (url: string = '', t: ResponsiveImageTransformations = {}) =>
  Object.keys(ds.breakpoints).reduce((sources, breakpoint) => {
    const width = t.width?.[breakpoint]
    const height = t.height?.[breakpoint]
    const size = t.size?.[breakpoint]
    const aspectRatio = t.aspectRatio?.[breakpoint]
    const align = t?.align
    const alignFill = t?.alignFill
    const background = t?.background
    const unSharpMask = t?.unSharpMask
    const sharpen = t?.sharpen
    const backgroundAuto = t?.backgroundAuto
    const excludeDPR = t?.excludeDPR
    const assetTransformation = t?.assetTransformation

    // Both dimensions are still required here even when an aspectRatio will drop one of them
    // downstream - the guard decides whether a breakpoint is rendered at all.
    if ((width && height) || size) {
      sources[breakpoint] = getImageTransformations(url, {
        align,
        alignFill,
        aspectRatio,
        background,
        width,
        height,
        size,
        sharpen,
        backgroundAuto,
        unSharpMask,
        excludeDPR,
        assetTransformation
      })
    }

    return sources
  }, {} as Responsive)
